import { apiRequest } from '../../shared/api/client';

/**
 * Inventory ledger + balances — mirrors Invetrak.Api.Endpoints.InventoryEndpoints.
 *   POST /api/v1/inventory/adjustments                    inventory:adjust
 *   GET  /api/v1/inventory/scan/location?code             inventory:read
 *   GET  /api/v1/inventory/scan/item?itemNumber           inventory:read
 *   GET  /api/v1/inventory/balances/{itemId}?warehouseId  inventory:read
 *   POST /api/v1/inventory/balances/{itemId}/rebuild       tenant:manage_settings
 *   GET  /api/v1/inventory/ledger?warehouseId&…           inventory:read
 *
 * Every call is scoped to a warehouse. The balance endpoint returns a zero-filled
 * row rather than 404 when nothing has ever been posted, so the UI can render the
 * grid without a special case.
 */

/** The ledger endpoint caps `take` at 500 server-side. */
export const LEDGER_TAKE_MAX = 500;
export const LEDGER_PAGE_SIZE = 100;

export interface BalanceDto {
  warehouseId: string;
  itemId: string;
  qtyOnHand: number;
  qtyQc: number;
  qtyMrb: number;
  qtyStaging: number;
  qtyWip: number;
  qtyReceiving: number;
  qtyInTransit: number;
  allocated: number;
  reserved: number;
  onOrder: number;
  backOrdered: number;
  availableToPromise: number;
  availableToReserve: number;
  lastModifiedUtc: string;
}

export interface LedgerEntryDto {
  inventoryTransactionId: string;
  warehouseId: string;
  tranCode: string;
  tranCodeName: string;
  /** INCREMENT | DECREMENT | MOVE | MOVEREV — the code's effect, from tbl_TransactionCodeType. */
  tranTypeCode: string;
  tranTypeName: string;
  /** +1, -1 or 0. Replaced a '+'/'-'/'0' character, which could not express MoveRev. */
  sign: number;
  itemId: string;
  locationId: string;
  balanceTypeId: number;
  lotId: string | null;
  serialId: string | null;
  licensePlateId: string | null;
  quantityDelta: number;
  uomId: string;
  referenceType: string | null;
  referenceId: string | null;
  performedByUserId: string | null;
  performedAtUtc: string;
  batchId: string | null;
  reason: string | null;
  isBeginningBalance: boolean;
  /**
   * Who posted it, resolved server-side from the tenant's user projection.
   *
   * Null for a posting no user owns — a background job, or somebody the projection no longer
   * holds. The row is still returned in that case: the join is a LEFT JOIN precisely so an
   * unresolvable user cannot quietly shorten the audit trail.
   */
  performedByName: string | null;
  performedByEmail: string | null;
  /** The numbers behind lotId / serialId / licensePlateId. Nothing could read the ids. */
  lotNumber: string | null;
  serialNumber: string | null;
  licensePlateNumber: string | null;
}

/**
 * There is no hardcoded list of adjustment codes here any more.
 *
 * There used to be three — ADJ_POS, ADJ_NEG and WRITE_OFF — invented in the first schema PR and
 * baked into the sproc, the endpoint and this file. None of them exists in the current product,
 * whose adjustment screen lists whatever is in the `Adj` group and lets the code's own effect decide
 * the direction. So the picker reads `listTransactionCodes({ groupCode: ADJUSTMENT_GROUP_CODE })`
 * and the request carries a positive magnitude — see AdjustmentRequest.quantity.
 */

/**
 * The bit of an item this feature needs. Narrower than ItemDto on purpose — the
 * adjustment form only wants to label the item and default its unit, so it
 * doesn't need to care about costing or tracking flags.
 */
export interface ItemSummary {
  itemId: string;
  itemNumber: string;
  description: string;
  primaryUomId: string;
}

export interface AdjustmentRequest {
  warehouseId: string;
  itemId: string;
  locationId: string;
  /**
   * A positive magnitude. Whether stock is added or removed comes from the transaction code's
   * effect, exactly as the current product's `sp_WebInventory_PostAdjustment` does it — so there is
   * no sign here to disagree with the code.
   */
  quantity: number;
  uomId: string;
  /** Three characters, from the `Adj` group. */
  tranCode: string;
  /**
   * The adjustment screen never sends this. The stock category is a property of the LOCATION — the
   * current product keeps it on the location row and every one of its inquiries rolls up by it — so
   * the server takes it from there. Asking the operator to name the category as well as the
   * location gives the two a way to disagree, with no way for the operator to know which one the
   * reports believe. Receiving and shipping will send it, because they post into a category the
   * location does not imply.
   */
  balanceTypeId?: number | null;
  /** Optional. The current product's adjustment screen has only Reference. */
  reason?: string | null;
  lotId?: string | null;
  serialId?: string | null;
  /** A scanned lot number. Registered on the spot if it is new, as the current product does. */
  lotNumber?: string | null;
  serialNumber?: string | null;
  /** Only after a `lot_needs_expiration` refusal — see AdjustRoute. */
  lotExpirationDateUtc?: string | null;
  licensePlateId?: string | null;
  /**
   * A plate that may not exist yet, started by this adjustment — the current product's "are you
   * starting a new license plate?" prompt. Positive transaction codes only, and `locationId` is
   * where the new plate stands.
   */
  newLicensePlateNumber?: string | null;
  referenceType?: string | null;
  referenceId?: string | null;
}

/**
 * What one scan into the "Location or License Plate" field turned out to be.
 *
 * One field, one barcode, two possible answers — the current product's CheckLocation tries the
 * locations first and falls back to the plates. Either way there is a `locationId` and a
 * `balanceTypeId`, because a plate is not a place: it resolves to whatever location it is standing
 * in and takes that location's category.
 */
export interface ScannedLocationDto {
  kind: 'location' | 'license_plate';
  locationId: string;
  locationCode: string;
  locationName: string | null;
  isForwardPick: boolean;
  isActive: boolean;
  balanceTypeId: number;
  balanceTypeCode: string;
  balanceTypeName: string;
  warehouseId: string;
  warehouseCode: string;
  licensePlateId: string | null;
  licensePlateNumber: string | null;
}

/** An item resolved from a scanned number, with the flags that decide which fields appear. */
export interface ScannedItemDto {
  itemId: string;
  itemNumber: string;
  description: string;
  isSerialized: boolean;
  isLotControlled: boolean;
  tracksExpiration: boolean;
  defaultShelfLifeDays: number | null;
  primaryUomId: string;
  primaryUomCode: string;
  primaryUomName: string;
  isActive: boolean;
}

/**
 * One request shape for four operations, because they differ only in where a plate appears: a plain
 * move names neither plate, loading names `toLicensePlateId`, unloading names
 * `fromLicensePlateId`, and moving stock between plates names both. The legacy system takes the
 * same view — its move procedure has `@IsFromLP` / `@IsToLP` rather than four procedures.
 *
 * `quantity` is a positive magnitude. A move carries its own direction, so a sign as well would
 * only create a way for the two to disagree.
 *
 * `toBalanceTypeId` omitted means "the same bucket", which is the common case. Sending 1 by default
 * would silently reclassify a QC move as on-hand.
 */
export interface MoveRequest {
  warehouseId: string;
  itemId: string;
  fromLocationId: string;
  toLocationId: string;
  quantity: number;
  uomId: string;
  /**
   * No stock category. It belongs to the LOCATION (tbl_Location.BalanceTypeID) and tenant 049 reads
   * it from there, ignoring anything sent. The screen used to ask for it with a "change bucket"
   * toggle, which was invented — legacy has no such concept in the move path, and it made it
   * possible to file on-hand stock into a QC rack by mistyping a dropdown.
   */
  lotId?: string | null;
  serialId?: string | null;
  /** What a screen actually has: the number somebody typed. The server resolves it. */
  lotNumber?: string | null;
  serialNumber?: string | null;
  fromLicensePlateId?: string | null;
  toLicensePlateId?: string | null;
  reason?: string | null;
}

export interface MoveLineDto {
  inventoryTransactionId: string;
  itemId: string;
  locationId: string;
  locationCode: string;
  balanceTypeId: number;
  lotId: string | null;
  serialId: string | null;
  licensePlateId: string | null;
  quantityDelta: number;
  uomId: string;
  uomCode: string;
  batchId: string;
  performedAtUtc: string;
}

/** Both ledger rows the move wrote — the negative side first. */
export interface MoveResponse {
  batchId: string;
  lines: MoveLineDto[];
}

export interface LedgerQuery {
  warehouseId: string;
  itemId?: string;
  fromUtc?: string;
  toUtc?: string;
  skip?: number;
  take?: number;
}

const COOKIE_INIT: RequestInit = { credentials: 'include' };

export async function getBalance(
  warehouseId: string,
  itemId: string,
  signal?: AbortSignal,
): Promise<BalanceDto> {
  const { data } = await apiRequest<BalanceDto>(
    `/v1/inventory/balances/${encodeURIComponent(itemId)}?warehouseId=${encodeURIComponent(warehouseId)}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

export async function queryLedger(
  query: LedgerQuery,
  signal?: AbortSignal,
): Promise<LedgerEntryDto[]> {
  const params = new URLSearchParams({ warehouseId: query.warehouseId });
  if (query.itemId) params.set('itemId', query.itemId);
  if (query.fromUtc) params.set('fromUtc', query.fromUtc);
  if (query.toUtc) params.set('toUtc', query.toUtc);
  if (query.skip) params.set('skip', String(query.skip));
  if (query.take) params.set('take', String(Math.min(query.take, LEDGER_TAKE_MAX)));

  const { data } = await apiRequest<LedgerEntryDto[]>(
    `/v1/inventory/ledger?${params.toString()}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/**
 * Resolves one scanned code to a location or a license plate.
 *
 * The adjustment screen calls this as the operator leaves the field and refuses to advance until it
 * succeeds — which is what lets somebody work the screen with a scanner without looking at it.
 */
export async function scanLocation(
  code: string,
  warehouseId?: string,
  signal?: AbortSignal,
): Promise<ScannedLocationDto> {
  const params = new URLSearchParams({ code });
  if (warehouseId) params.set('warehouseId', warehouseId);
  const { data } = await apiRequest<ScannedLocationDto>(
    `/v1/inventory/scan/location?${params.toString()}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/** The item behind a scanned number, with its description, stocking unit and tracking flags. */
export async function scanItem(itemNumber: string, signal?: AbortSignal): Promise<ScannedItemDto> {
  const { data } = await apiRequest<ScannedItemDto>(
    `/v1/inventory/scan/item?itemNumber=${encodeURIComponent(itemNumber)}`,
    { method: 'GET', signal, ...COOKIE_INIT },
  );
  return data;
}

/** Posts an adjustment and returns the resulting balance. */
export async function postAdjustment(
  body: AdjustmentRequest,
  signal?: AbortSignal,
): Promise<BalanceDto> {
  const { data } = await apiRequest<BalanceDto>('/v1/inventory/adjustments', {
    method: 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

export async function postMove(body: MoveRequest, signal?: AbortSignal): Promise<MoveResponse> {
  const { data } = await apiRequest<MoveResponse>('/v1/inventory/moves', {
    method: 'POST',
    body,
    signal,
    ...COOKIE_INIT,
  });
  return data;
}

/** Recalculates the cached balance from the ledger. Needs tenant:manage_settings. */
export async function rebuildBalance(
  warehouseId: string,
  itemId: string,
  signal?: AbortSignal,
): Promise<BalanceDto> {
  const { data } = await apiRequest<BalanceDto>(
    `/v1/inventory/balances/${encodeURIComponent(itemId)}/rebuild?warehouseId=${encodeURIComponent(warehouseId)}`,
    { method: 'POST', signal, ...COOKIE_INIT },
  );
  return data;
}
